/* ═══════════════════════════════════════════════════════════════
   PiARIS — Audit Report View
   Top-level pre-remediation audit workbench. Groups all scan
   findings by page, applies filters, renders issue cards,
   and provides HTML / Word export buttons.
   © 2026 Pi Consultants · pi-consulting.us
   ═══════════════════════════════════════════════════════════════ */
import React, { useState, useMemo } from "react";
import { P } from "../constants";
import { sevToPriority, isSitewideIssue, wcagToRiskLevel,
  globalIssueNumbers } from "../helpers";
import { W } from "../constants";
import { AuditIssueCard } from "./AuditIssueCard";
import { AuditPageSection } from "./AuditPageSection";
import { AuditFilters } from "./AuditFilters";
import { downloadAuditHtml, downloadAuditDoc } from "./exporters";

var E = React.createElement;

/** AuditReportView
    Props:
      wp:        web pages array
      dc:        documents array
      templateStats: from App.jsx useMemo
      domain:    string
      scanMeta:  optional scan metadata (sessionId, createdAt) */
function AuditReportView(p) {
  var wp = p.wp || [];
  var dc = p.dc || [];
  /* Combine documents into the working list (normalized to page shape) so doc
     findings flow through the same aggregation. Without this the Doc Audit
     Report only read wp and showed "0 findings across 0 pages". */
  var itemNoun = (wp.length && dc.length) ? "items" : (dc.length ? "documents" : "pages");
  if (dc.length) {
    wp = wp.concat(dc.map(function(d) {
      return Object.assign({}, d, { n: d.name || d.n || d.url || "Document", issues: d.issues || [] });
    }));
  }
  var templateStats = p.templateStats || { groups: {}, templateGroupCount: 0 };
  var domain = p.domain || "";
  var scanMeta = p.scanMeta || {};
  var darkMode = !!p.darkMode;

  /* Filter state */
  var _priority = useState("all"),   priority    = _priority[0],   setPriority    = _priority[1];
  var _sitewide = useState(false),   sitewideOnly = _sitewide[0],   setSitewideOnly = _sitewide[1];
  var _highRisk = useState(false),   highRiskOnly = _highRisk[0],   setHighRiskOnly = _highRisk[1];
  var _openOnly = useState(true),    openOnly    = _openOnly[0],    setOpenOnly    = _openOnly[1];
  var _search   = useState(""),      search      = _search[0],      setSearch      = _search[1];

  /* Global issue numbering — same across renders for the same wp */
  var numbers = useMemo(function() { return globalIssueNumbers(wp); }, [wp]);

  /* Total issue count across all pages */
  var totalAll = useMemo(function() {
    var n = 0;
    wp.forEach(function(pg) { n += (pg.issues || []).length; });
    return n;
  }, [wp]);

  /* Apply filters and group by page */
  var filteredByPage = useMemo(function() {
    var q = search.trim().toLowerCase();
    return wp.map(function(pg, pi) {
      var kept = (pg.issues || []).filter(function(iss) {
        /* Priority filter */
        if (priority !== "all") {
          var pri = sevToPriority(iss.sev || "major");
          if (pri.code !== priority) return false;
        }
        /* Sitewide filter */
        if (sitewideOnly && !isSitewideIssue(iss, templateStats)) return false;
        /* High risk filter */
        if (highRiskOnly) {
          var r = wcagToRiskLevel(iss.sev, (W[iss.wcag] || {}).l);
          if (r !== "High Risk") return false;
        }
        /* Open only filter */
        if (openOnly && iss.remediated) return false;
        /* Search */
        if (q) {
          var hay = ((iss.desc || "") + " " + (iss.wcag || "") + " " + (iss.selector || "") + " " + (iss.el || "")).toLowerCase();
          if (hay.indexOf(q) === -1) return false;
        }
        return true;
      });
      return { page: pg, pageIdx: pi, issues: kept };
    }).filter(function(g) { return g.issues.length > 0; });
  }, [wp, priority, sitewideOnly, highRiskOnly, openOnly, search, templateStats]);

  var totalShown = useMemo(function() {
    return filteredByPage.reduce(function(x, g) { return x + g.issues.length; }, 0);
  }, [filteredByPage]);

  /* Has data check */
  var hasData = wp.length > 0 || dc.length > 0;

  /* Header with export buttons */
  var header = E("header", {
    style: {
      display: "flex",
      alignItems: "center",
      justifyContent: "space-between",
      flexWrap: "wrap",
      gap: 12,
      marginBottom: 16,
      paddingBottom: 14,
      borderBottom: "1px solid " + P.bdr
    }
  },
    E("div", null,
      E("div", { style: { fontSize: 10, fontWeight: 800, color: P.slateL, textTransform: "uppercase", letterSpacing: ".08em", marginBottom: 4 } }, "Pre-Remediation"),
      E("h2", { style: { fontSize: 22, fontWeight: 900, margin: 0, color: P.slate, lineHeight: 1.2 } }, "Audit Report"),
      E("p", { style: { fontSize: 11, color: P.slateM, margin: "4px 0 0" } },
        hasData ? (totalAll.toLocaleString() + " findings across " + wp.length.toLocaleString() + " " + itemNoun + " \u00B7 " + domain) : "No scan data loaded")
    ),
    /* Actions: Load Scan (always) + Export buttons (only when data loaded) */
    E("div", { style: { display: "flex", gap: 8, flexWrap: "wrap" } },
      p.onLoadScan ? E("button", {
        onClick: p.onLoadScan,
        "aria-label": "Load a scan into the audit report",
        style: {
          minHeight: 44,
          padding: "10px 18px",
          borderRadius: 10,
          border: "1.5px solid " + P.bdr,
          background: P.card,
          color: P.slateM,
          fontSize: 12,
          fontWeight: 700,
          cursor: "pointer",
          fontFamily: "inherit"
        }
      }, "\uD83D\uDCC2 Load Scan") : null,
      hasData ? E("button", {
        onClick: function() { downloadAuditHtml(wp, dc, { domain: domain, scanDate: new Date().toLocaleDateString(), sessionId: scanMeta.sessionId || "", templateStats: templateStats }); },
        style: {
          minHeight: 44,
          padding: "10px 18px",
          borderRadius: 10,
          border: "1.5px solid " + P.pri,
          background: P.priLt,
          color: P.pri,
          fontSize: 12,
          fontWeight: 800,
          cursor: "pointer",
          fontFamily: "inherit"
        }
      }, "\uD83D\uDCE5 Download HTML") : null,
      hasData ? E("button", {
        onClick: function() { downloadAuditDoc(wp, dc, { domain: domain, scanDate: new Date().toLocaleDateString(), sessionId: scanMeta.sessionId || "", templateStats: templateStats }); },
        style: {
          minHeight: 44,
          padding: "10px 18px",
          borderRadius: 10,
          border: "none",
          background: "linear-gradient(135deg," + P.pri + "," + P.accent + ")",
          color: "#fff",
          fontSize: 12,
          fontWeight: 800,
          cursor: "pointer",
          fontFamily: "inherit",
          boxShadow: "0 2px 8px " + P.pri + "30"
        }
      }, "\uD83D\uDCC4 Download Word (.doc)") : null
    )
  );

  /* About-this-report honesty footer */
  var aboutNote = E("div", {
    style: {
      background: P.amberLt,
      border: "1px solid " + P.amber + "40",
      borderRadius: 10,
      padding: "12px 16px",
      marginTop: 16,
      fontSize: 11,
      color: P.amberDk || P.amber,
      lineHeight: 1.6
    }
  },
    E("strong", null, "About this report: "),
    "This pre-remediation audit is generated from automated scans (axe-core + HTML_CodeSniffer) and the PiARIS WCAG knowledge base. Component classification is heuristic and based on CSS selectors. For a legally defensible Letter of Conformance, a human audit pass is recommended to verify findings and component boundaries."
  );

  /* Empty state */
  if (!hasData) {
    return E("div", { role: "main", "aria-label": "Audit Report" },
      header,
      E("div", {
        style: {
          background: P.card,
          border: "2px dashed " + P.bdr,
          borderRadius: 14,
          padding: "52px 24px 48px",
          textAlign: "center"
        }
      },
        E("div", { "aria-hidden": "true", style: { fontSize: 56, marginBottom: 12 } }, "\uD83D\uDCCB"),
        E("h3", { style: { fontSize: 18, fontWeight: 800, margin: "0 0 8px", color: P.slate } }, "No scan data loaded"),
        E("p", { style: { fontSize: 12, color: P.slateM, margin: "0 0 18px", maxWidth: 420, marginLeft: "auto", marginRight: "auto", lineHeight: 1.6 } },
          "Load a previously saved scan, or run a fresh scan with the PiARIS browser extension to see findings organized by page with exportable Word / HTML reports."),
        /* Diagnostic state readout — helps confirm what's actually loaded */
        E("div", {
          style: {
            display: "inline-flex",
            gap: 10,
            padding: "6px 14px",
            borderRadius: 8,
            background: P.bgS,
            fontSize: 10,
            fontWeight: 600,
            color: P.slateL,
            marginBottom: 20,
            fontFamily: "monospace"
          }
        },
          E("span", null, "web pages: " + wp.length),
          E("span", null, "\u00B7"),
          E("span", null, "documents: " + dc.length),
          domain ? E("span", null, "\u00B7") : null,
          domain ? E("span", null, "domain: " + domain) : null
        ),
        p.onLoadScan ? E("div", null,
          E("button", {
            onClick: p.onLoadScan,
            style: {
              minHeight: 44,
              padding: "12px 24px",
              borderRadius: 10,
              border: "none",
              background: "linear-gradient(135deg," + P.pri + "," + P.accent + ")",
              color: "#fff",
              fontSize: 13,
              fontWeight: 800,
              cursor: "pointer",
              fontFamily: "inherit",
              boxShadow: "0 4px 16px " + P.pri + "40"
            }
          }, "\uD83D\uDCC2 Load Scan")
        ) : null
      )
    );
  }

  /* Main content */
  /* v1.0: Pi Consultants Executive Summary panel — rendered when
     scanMeta.aiExecutiveSummary is populated (by the ai-review scan-job).
     Structured as: headline, overview, top risks, remediation approach,
     Pi Consultants offer + contact CTA. Honest about source and date. */
  var aiExec = scanMeta.aiExecutiveSummary;
  var execSummaryPanel = (aiExec && aiExec.summary) ? (function(){
    var s = aiExec.summary;
    return E("section", {
      "aria-label": "Pi Consultants Executive Summary",
      style: {
        marginBottom: 20,
        padding: "22px 26px",
        borderRadius: 14,
        background: "linear-gradient(135deg,#EEF2FF,#F0F9FF,#ECFDF5)",
        border: "1.5px solid " + P.pri + "40",
        boxShadow: "0 4px 24px rgba(79,70,229,0.08)"
      }
    },
      /* Header strip */
      E("div", { style: { display: "flex", alignItems: "center", gap: 10, marginBottom: 12, flexWrap: "wrap" } },
        E("span", { style: { fontSize: 24 }, "aria-hidden": "true" }, "\uD83C\uDFAF"),
        E("div", { style: { flex: 1, minWidth: 200 } },
          E("div", { style: { fontSize: 15, fontWeight: 900, color: P.slate, letterSpacing: ".01em" } }, "Pi Consultants Executive Summary"),
          E("div", { style: { fontSize: 10, color: P.slateM, marginTop: 2 } },
            "AI-generated \u00B7 " +
            (aiExec.model || "Claude Sonnet") + " \u00B7 " +
            (aiExec.generatedAt ? new Date(aiExec.generatedAt).toLocaleDateString() : "today")
          )
        ),
        s.estimatedEffort ? E("div", {
          style: { padding: "6px 12px", borderRadius: 8, background: "#fff", border: "1px solid " + P.pri + "30" }
        },
          E("div", { style: { fontSize: 9, color: P.slateL, fontWeight: 700, textTransform: "uppercase" } }, "Estimated Effort"),
          E("div", { style: { fontSize: 14, fontWeight: 800, color: P.pri } }, s.estimatedEffort.days + " day" + (s.estimatedEffort.days === 1 ? "" : "s")),
          E("div", { style: { fontSize: 8, color: P.slateL } }, "confidence: " + (s.estimatedEffort.confidence || "medium"))
        ) : null
      ),
      /* Headline finding */
      s.headlineFinding ? E("div", {
        style: {
          padding: "12px 16px",
          borderRadius: 10,
          background: "#fff",
          border: "1px solid " + P.bdr,
          marginBottom: 14,
          fontSize: 14,
          fontWeight: 700,
          color: P.slate,
          lineHeight: 1.55
        }
      },
        E("span", { "aria-hidden": "true", style: { marginRight: 8 } }, "\u26A0\uFE0F"),
        s.headlineFinding
      ) : null,
      /* Overview */
      s.overview ? E("div", {
        style: { fontSize: 12.5, color: P.slate, lineHeight: 1.7, marginBottom: 16, whiteSpace: "pre-wrap" }
      }, s.overview) : null,
      /* Top risks — grid of risk cards */
      (Array.isArray(s.topRisks) && s.topRisks.length > 0) ? E("div", { style: { marginBottom: 16 } },
        E("div", { style: { fontSize: 11, fontWeight: 800, color: P.slate, textTransform: "uppercase", letterSpacing: ".04em", marginBottom: 8 } }, "Top Risks, Ranked"),
        E("div", { style: { display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(260px,1fr))", gap: 10 } },
          s.topRisks.slice(0, 5).map(function(risk, ri) {
            return E("div", { key: ri, style: { padding: "10px 14px", borderRadius: 8, background: "#fff", border: "1px solid " + P.bdr } },
              E("div", { style: { display: "flex", alignItems: "center", gap: 6, marginBottom: 4 } },
                E("span", { style: { width: 22, height: 22, borderRadius: "50%", background: P.pri, color: "#fff", display: "inline-flex", alignItems: "center", justifyContent: "center", fontSize: 11, fontWeight: 800 } }, ri + 1),
                E("div", { style: { fontSize: 12, fontWeight: 800, color: P.slate, flex: 1 } }, risk.title || "Risk " + (ri + 1))
              ),
              risk.impact ? E("div", { style: { fontSize: 11, color: P.slateM, marginBottom: 4, lineHeight: 1.5 } }, risk.impact) : null,
              risk.affectedPages ? E("div", { style: { fontSize: 10, color: P.slateL, marginBottom: 4 } }, "Affects " + risk.affectedPages + " page" + (risk.affectedPages === 1 ? "" : "s")) : null,
              risk.recommendedAction ? E("div", { style: { fontSize: 10.5, color: P.pri, fontWeight: 600, marginTop: 6, paddingTop: 6, borderTop: "1px dashed " + P.bdr } },
                E("span", { "aria-hidden": "true", style: { marginRight: 4 } }, "\u2192"),
                risk.recommendedAction
              ) : null
            );
          })
        )
      ) : null,
      /* Remediation approach */
      s.remediationApproach ? E("div", {
        style: { marginBottom: 14, padding: "12px 16px", borderRadius: 10, background: "#fff", border: "1px solid " + P.bdr }
      },
        E("div", { style: { fontSize: 11, fontWeight: 800, color: P.slate, textTransform: "uppercase", letterSpacing: ".04em", marginBottom: 6 } }, "Recommended Approach"),
        E("div", { style: { fontSize: 12, color: P.slateM, lineHeight: 1.65, whiteSpace: "pre-wrap" } }, s.remediationApproach)
      ) : null,
      /* Pi Consultants offer + contact */
      s.piConsultantsOffer ? E("div", {
        style: {
          padding: "14px 18px",
          borderRadius: 10,
          background: "linear-gradient(135deg," + P.pri + "," + P.accent + ")",
          color: "#fff"
        }
      },
        E("div", { style: { fontSize: 11, fontWeight: 800, textTransform: "uppercase", letterSpacing: ".05em", marginBottom: 6, opacity: 0.9 } }, "\uD83E\uDD1D How Pi Consultants Can Help"),
        E("div", { style: { fontSize: 12.5, lineHeight: 1.65, marginBottom: 10, whiteSpace: "pre-wrap" } }, s.piConsultantsOffer),
        E("a", {
          href: "mailto:Piaris.Support@piconsultants.us?subject=" + encodeURIComponent("Accessibility engagement - " + (domain || "")),
          style: {
            display: "inline-block",
            padding: "8px 16px",
            borderRadius: 8,
            background: "#fff",
            color: P.pri,
            fontSize: 11,
            fontWeight: 800,
            textDecoration: "none",
            letterSpacing: ".02em"
          }
        }, "\u2709 Piaris.Support@piconsultants.us")
      ) : null
    );
  })() : null;

  return E("div", { role: "main", "aria-label": "Audit Report" },
    header,
    execSummaryPanel,
    E(AuditFilters, {
      priority: priority, onPriority: setPriority,
      sitewideOnly: sitewideOnly, onSitewide: setSitewideOnly,
      highRiskOnly: highRiskOnly, onHighRisk: setHighRiskOnly,
      openOnly: openOnly, onOpenOnly: setOpenOnly,
      search: search, onSearch: setSearch,
      totalShown: totalShown, totalAll: totalAll
    }),
    /* Per-page sections */
    filteredByPage.length === 0 ? E("div", {
      style: {
        padding: 40,
        textAlign: "center",
        color: P.slateM,
        fontSize: 12,
        background: P.bgS,
        borderRadius: 10,
        border: "1px dashed " + P.bdr
      }
    }, "No issues match your filters.") :
    filteredByPage.map(function(g) {
      return E(AuditPageSection, {
        key: g.pageIdx,
        page: g.page,
        pageIdx: g.pageIdx,
        issueCount: g.issues.length,
        thumbnail: g.page.screenshot || g.page.thumbnail,
        isCore: g.pageIdx < 3
      },
        g.issues.map(function(iss, localIdx) {
          /* Find original index in unfiltered page for stable key + numbering */
          var origIdx = (g.page.issues || []).indexOf(iss);
          if (origIdx < 0) origIdx = localIdx;
          var num = numbers[g.pageIdx + "_" + origIdx];
          /* v0.6.10: Visual preview priority:
             1. Per-issue before screenshot (remediation workflow)
             2. Page-level JPEG thumbnail (old screenshot path)
             3. DOM snapshot (current extension — renders in sandboxed iframe)
             If none exist, AuditIssueCard hides the preview block entirely. */
          var shotSrc = iss.screenshotBefore || g.page.screenshot || g.page.screenshotUrl || g.thumbnail || "";
          var shot = shotSrc ? {
            src: shotSrc,
            alt: (iss.screenshotBefore ? "Before screenshot for issue " : "Page screenshot containing issue ") + num
          } : null;
          return E(AuditIssueCard, {
            key: g.pageIdx + "-" + origIdx,
            issue: iss,
            issueNumber: num,
            pageIdx: g.pageIdx,
            issIdx: origIdx,
            templateStats: templateStats,
            screenshot: shot,
            domSnapshot: !shot ? (g.page.dom || null) : null,
            page: g.page,
            createdIn: scanMeta.sessionId || "Scan session",
            updatedIn: scanMeta.sessionId || "Scan session",
            /* sessionId enables the multi-screenshot IssueScreenshots widget.
               Without it the widget hides (used for exported reports that
               don't have backend write access). */
            sessionId: scanMeta.sessionId || null,
            readOnly: p.readOnly === true,
            darkMode: darkMode
          });
        })
      );
    }),
    aboutNote
  );
}

export { AuditReportView };
